/*
Overview: https://commons.wikimedia.org/w/index.php?search=onedrive&title=Special:MediaSearch&go=Go&type=image
Overview II: https://developer.microsoft.com/en-us/fluentui#/styles/web/file-type-icons

https://commons.wikimedia.org/wiki/File:OneDrive_Folder_Icon.svg
https://en.m.wikipedia.org/wiki/File:.pptx_icon_(2019).svg
https://en.m.wikipedia.org/wiki/File:.docx_icon.svg
https://en.m.wikipedia.org/wiki/File:.xlsx_icon.svg
https://commons.wikimedia.org/wiki/File:Photo_OneDrive_icon.svg
https://commons.wikimedia.org/wiki/File:.pdf_OneDrive_icon.svg
https://commons.wikimedia.org/wiki/File:.zip_OneDrive_icon.svg
https://commons.wikimedia.org/wiki/File:Generic_File_OneDrive_icon.svg

*/

import {
  ArrowSyncCircleRegular,
  DocumentGlobeFilled,
  DocumentGlobeRegular,
  EditOffRegular,
  LockClosedRegular,
  ScreenshotFilled,
  ScreenshotRegular,
} from '@fluentui/react-icons';
import { ITEM_MIMETYPES } from '../constants';
import { EFolderType } from '../types/folders.types';
import type { TItemType } from '../types/items.types';
import ProductAvatar from './ProductAvatar';

// Assets

import folder from '../assets/folder.svg';
import docx from '../assets/docx.svg';
import generic from '../assets/generic.svg';
import image from '../assets/image.svg';
import pdf from '../assets/pdf.svg';
import pptx from '../assets/pptx.svg';
import xlsx from '../assets/xlsx.svg';
import zip from '../assets/zip.svg';
import dotx from '../assets/dotx.svg';
import xltx from '../assets/xltx.svg';
import potx from '../assets/potx.svg';
import block from '../assets/block.svg';
import inlineBlock from '../assets/inline-block.svg';
/*
fake Microsoft file icons:

import folder from '../assets/folder.svg';
import docx from '../assets/fake/docx.png';
import generic from '../assets/fake/generic.png';
import image from '../assets/fake/image.png';
import pdf from '../assets/fake/pdf.png';
import pptx from '../assets/fake/pptx.png';
import xlsx from '../assets/fake/xlsx.png';
import zip from '../assets/fake/zip.png';
import block from '../assets/fake/block.png';
*/

// eslint-disable-next-line react-refresh/only-export-components
export const iconMap = { image, pdf, pptx, xlsx, zip, docx, dotx, xltx, potx } as const;

type TSize = 'medium' | 'small' | 'auto';
type TProps = {
  type: TItemType;
  path: string[];
  isBlock: boolean;
  isFrozen: boolean;
  isTemplate: boolean;
  folderType?: EFolderType;
  mimetype?: string;
  size?: TSize;
  name?: string;
  id?: string;
};
const FileIcon = ({
  type,
  path,
  isBlock,
  isFrozen,
  isTemplate,
  folderType,
  mimetype,
  size = 'medium',
  name,
  id,
}: TProps) => {
  // https://www.computerhope.com/issues/ch001789.htm
  const sizePx = size === 'medium' ? '30px' : size === 'small' ? '25px' : 'auto';
  const sizeIconPx = size === 'medium' ? '20px' : size === 'small' ? '16px' : '50%';

  let src: string;
  if (type === 'FOLDER') {
    src = folder;
    if (folderType === EFolderType.PRODUCT && path.length === 0 && id && name)
      return <ProductAvatar id={id} name={name} size={size === 'small' ? 24 : 32} />;
    if (folderType === EFolderType.SNAPSHOT && path.length === 3)
      return isFrozen ? (
        <ScreenshotFilled className="text-brand-80" style={{ width: sizePx, height: sizePx }} />
      ) : (
        <ScreenshotRegular className="text-brand-80" style={{ width: sizePx, height: sizePx }} />
      );
    else if (folderType === EFolderType.COUNTRY && path.length === 2)
      return isFrozen ? (
        <DocumentGlobeFilled className="text-brand-80" style={{ width: sizePx, height: sizePx }} />
      ) : (
        <DocumentGlobeRegular className="text-brand-80" style={{ width: sizePx, height: sizePx }} />
      );
  } else if (type === 'INLINE_BLOCK') {
    src = inlineBlock;
  } else if (['DOCUMENT', 'REPLICA_DOCUMENT'].includes(type)) {
    if (name?.endsWith('.xlsx')) src = xlsx;
    else if (isBlock) src = block;
    else if (isTemplate) src = dotx;
    else src = docx;
  } else if (!mimetype) {
    src = generic;
  } else {
    src = generic;
    for (const [filetype, mimetypeList] of Object.entries(ITEM_MIMETYPES)) {
      if (mimetypeList.includes(mimetype)) {
        src = iconMap[filetype as keyof typeof iconMap];
        break;
      }
    }
  }

  return (
    <div className="relative" style={{ width: sizePx, height: sizePx }}>
      <img src={src} alt="File" style={{ width: sizePx, height: sizePx }} />
      {isFrozen ? (
        <LockClosedRegular
          style={{
            position: 'absolute',
            bottom: '-3px',
            right: '-3px',
            width: sizeIconPx,
            height: sizeIconPx,
            backgroundColor: 'white',
            borderRadius: '100px',
          }}
        />
      ) : ['REPLICA_DOCUMENT', 'REPLICA_FILE'].includes(type) ? (
        <ArrowSyncCircleRegular
          style={{
            position: 'absolute',
            bottom: '-3px',
            right: '-3px',
            width: sizeIconPx,
            height: sizeIconPx,
            backgroundColor: 'white',
            borderRadius: '100px',
          }}
        />
      ) : type === 'FILE' &&
        mimetype &&
        [ITEM_MIMETYPES.docx, ITEM_MIMETYPES.dotx].some((list) => list.includes(mimetype)) ? (
        <EditOffRegular
          style={{
            position: 'absolute',
            bottom: '-3px',
            right: '-3px',
            width: sizeIconPx,
            height: sizeIconPx,
            backgroundColor: 'white',
            borderRadius: '100px',
          }}
        />
      ) : null}
    </div>
  );
  // <ArrowSyncCircleRegular style={{ position: 'absolute', bottom: '-2px', right: '-2px', zIndex: 20, backgroundColor: 'white', borderRadius: '100px' }} />
  // <img src={src} alt="File" className={noMinWidth ? '' : classes.root} />
};

export default FileIcon;
